import React, { useEffect, useState } from "react";
import { fetchFeedBlocks } from "../../api";
import { avatarTint } from "./PostCard";

/**
 * MajlisSidebar — the mockup's aside, honest edition: an about card and the
 * real rules of the feed. The mockup's member/online stats, join button and
 * "active مجالس" list are omitted — no communities backend, no fake numbers.
 *
 * Rendered in the 312px left column (RTL grid) ≥920px; hidden below.
 */
const RULES = [
  ["الفصحى وحدَها", "تُحجَبُ المنشوراتُ التي فيها عامّيّةٌ حتى تُصحَّح — والمدقّقُ يقترحُ البديلَ الفصيح."],
  ["تعلّمِ الكتابةَ أوّلًا", "تُفتَحُ الكتابةُ بعدَ إتمامِ دروسِ الطباعةِ حتى الحركات."],
  ["التشكيلُ محمود", "المنشورُ المشكولُ بالكاملِ ينالُ وسامَ «مشكول»."],
  ["صحِّحْ برِفق", "الملاحظاتُ الخاصّةُ تصلُ صاحبَ المنشورِ وحدَه — لا تصحيحَ علنيًّا."],
  ["احترمِ المجلس", "بلِّغْ عن أيِّ إساءةٍ — تُراجَعُ البلاغاتُ يوميًّا."],
];

/**
 * BlockedUsersCard — once a user is blocked their content is hidden
 * everywhere, so this list is the only route back to their profile (and
 * إلغاء الحظر). Rendered in the sidebar ≥920px and at the bottom of the
 * main column on phones (where the sidebar is hidden). refreshKey bumps
 * the fetch so a block made in this session appears without a reload.
 */
export function BlockedUsersCard({ onOpenProfile = null, refreshKey = 0 }) {
  const [blocked, setBlocked] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetchFeedBlocks()
      .then((d) => !cancelled && setBlocked(d.blocked || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (blocked.length === 0) return null;
  return (
    <div
      className="rounded-[14px] px-4 py-3.5"
      style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
    >
      <p className="font-plex text-[13px] font-bold mb-1.5" style={{ color: "var(--mj-muted)" }}>
        المحظورون
      </p>
      {blocked.map((user) => (
        <button
          key={user.id}
          onClick={() => onOpenProfile && onOpenProfile(user)}
          className="flex items-center gap-2.5 py-2 w-full text-right font-plex text-[13.5px]"
          style={{ color: "var(--mj-muted)", borderBottom: "1px dashed var(--mj-line)" }}
          title="افتح الملف لإلغاء الحظر"
        >
          <span
            className="w-6 h-6 rounded-full grid place-items-center text-xs font-bold shrink-0"
            style={{ ...avatarTint(user.id), border: "1px solid var(--mj-line)" }}
            aria-hidden="true"
          >
            {user.initial || "؟"}
          </span>
          <span className="truncate flex-1">{user.display_name}</span>
          <span className="text-[11px] shrink-0" style={{ color: "var(--mj-flag)" }}>
            محظور
          </span>
        </button>
      ))}
    </div>
  );
}

export default function MajlisSidebar({ onOpenProfile = null, refreshKey = 0 }) {
  return (
    <aside className="sticky top-[76px] self-start flex-col gap-4 hidden min-[920px]:flex">
      {/* about card */}
      <div
        className="rounded-[14px] overflow-hidden"
        style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
      >
        <div
          className="h-[58px] flex items-end px-4 pb-2 font-scheherazade font-bold text-[21px]"
          style={{
            background: "linear-gradient(120deg, var(--mj-ink-6), var(--mj-banner-end))",
            borderBottom: "1px solid var(--mj-gold-line)",
            color: "var(--mj-cream)",
          }}
        >
          المجلس
        </div>
        <div className="px-4 py-3.5">
          <p className="font-plex text-[13.5px] leading-[1.9]" style={{ color: "var(--mj-muted)" }}>
            ساحةُ مُراجَعة للكتابةِ بالفصحى — اكتُبْ، وصوِّتْ، وتعلَّمْ من تصحيحاتِ
            المدقّقِ الخاصّةِ بك. كلُّ ما يُنشَرُ هنا فصيحٌ أو في طريقِه إلى الفصاحة.
          </p>
        </div>
      </div>

      {/* rules card */}
      <div
        className="rounded-[14px] px-4 py-3.5"
        style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
      >
        <p className="font-plex text-[13px] font-bold mb-1.5" style={{ color: "var(--mj-muted)" }}>
          قوانين المجلس
        </p>
        <ol className="mj-rules font-plex">
          {RULES.map(([title, detail]) => (
            <li key={title}>
              <div>
                <b>{title}</b>
                <span>{detail}</span>
              </div>
            </li>
          ))}
        </ol>
      </div>
      {/* blocked users — the route back to unblock */}
      <BlockedUsersCard onOpenProfile={onOpenProfile} refreshKey={refreshKey} />
    </aside>
  );
}
